import {
  BoxIcon,
  Building2Icon,
  ClockIcon,
  LayersIcon,
  PlusIcon,
  SettingsIcon,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Outlet, useLocation, useNavigate } from 'react-router';

import { InfoTooltip } from '@/components/atoms/InfoTooltip/InfoTooltip';
import { ReadOnlyBanner } from '@/components/atoms/ReadOnlyBanner/ReadOnlyBanner';
import {
  BUNDLE_CREATE,
  MODAL_DEPARTMENT_CREATE,
} from '@/components/organisms/Modals/ModalConductor';
import { SettingsNavigation } from '@/components/organisms/Navbar/SettingsNavigation/SettingsNavigation';
import {
  PageSubHeader,
  PageSubHeaderActions,
  PageSubHeaderBreadcrumbs,
  PageSubHeaderTabs,
} from '@/components/organisms/PageSubHeader/PageSubHeader';
import Layout from '@/components/templates/Layout';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbList,
  BreadcrumbPage,
} from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useDeskId } from '@/hooks/useDeskId';
import useDesks from '@/hooks/useDesks';
import { pushModal } from '@/redux/modals/actions';
import { getPermissions } from '@/redux/permissions/selectors';
import { selectAccountSlug } from '@/redux/session/selectors';
import { actions } from '@/shared/types/permissions';
import { RootState } from '@/shared/types/state';

// Icon-only tabs below lg (1024px); the label is shown at ≥lg. Predetermined icons per tab.
const ENVIRONMENT_TABS = [
  { value: 'business_hours', labelKey: 'common.business_hours', icon: ClockIcon },
  { value: 'departments', labelKey: 'common.departments', icon: Building2Icon },
  { value: 'context_bundles', labelKey: 'common.context_bundles', icon: LayersIcon },
  { value: 'settings', labelKey: 'common.settings', icon: SettingsIcon },
] as const;

const EnvironmentRouter: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const deskId = useDeskId();
  const slug = useSelector(selectAccountSlug);

  const { desks } = useDesks(deskId);
  const { pathname } = location;
  const pageArray = pathname.split('/');
  const page = pageArray[pageArray.length - 1];

  const canWriteBundles = useSelector((state: RootState) =>
    getPermissions(state, 'bundles', actions.WRITE)
  );

  const canWriteDepartments = useSelector((state: RootState) =>
    getPermissions(state, 'departments', actions.WRITE)
  );

  const canWriteBusinessHours = useSelector((state: RootState) =>
    getPermissions(state, 'business_hours', actions.WRITE)
  );

  const canWriteDesks = useSelector((state: RootState) =>
    getPermissions(state, 'desks', actions.WRITE)
  );

  const selectedDesk = desks?.find((desk) => desk.desk_id === deskId)?.name;

  const getCurrentTab = () => {
    if (pathname.includes('/context_bundles')) {
      return 'context_bundles';
    }
    if (pathname.includes('/business_hours')) {
      return 'business_hours';
    }
    if (pathname.includes('/departments')) {
      return 'departments';
    }
    if (pathname.includes('/settings')) {
      return 'settings';
    }
    return 'business_hours';
  };

  const READ_ONLY_BY_TAB: Record<string, boolean> = {
    business_hours: !canWriteBusinessHours,
    context_bundles: !canWriteBundles,
    departments: !canWriteDepartments,
    settings: !canWriteDesks,
  };
  const isReadOnly = READ_ONLY_BY_TAB[getCurrentTab()] ?? false;

  const buttonConfig: Record<string, { onClick: () => void; disabled: boolean; label: string }> = {
    context_bundles: {
      onClick: () => dispatch(pushModal(BUNDLE_CREATE)),
      disabled: !canWriteBundles,
      label: 'common.add',
    },
    departments: {
      onClick: () => dispatch(pushModal(MODAL_DEPARTMENT_CREATE)),
      disabled: !canWriteDepartments,
      label: 'common.create',
    },
    business_hours: {
      onClick: () => navigate(`/${slug}/environments/${deskId}/business_hours/draft`),
      disabled: !canWriteBusinessHours,
      label: 'common.add',
    },
  };
  const activeButton = buttonConfig[page];

  return (
    <Layout>
      <PageSubHeader
        inlineTabs={true}
        breadcrumbs={
          <PageSubHeaderBreadcrumbs>
            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbPage>
                    <div className="flex h-8 w-8 items-center justify-center">
                      <BoxIcon size={16} className="text-muted-foreground" />
                    </div>
                    <div className="mr-4 ml-2 h-4 w-px bg-border" />
                    <span className="text-sm leading-5 font-normal">
                      {selectedDesk ?? t('common.environments')}
                    </span>
                  </BreadcrumbPage>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>
          </PageSubHeaderBreadcrumbs>
        }
        tabs={
          <PageSubHeaderTabs className="min-w-0 flex-1 overflow-hidden">
            <Tabs value={getCurrentTab()} className="w-full">
              <TabsList className="min-w-0 shrink-0">
                {ENVIRONMENT_TABS.map(({ value, labelKey, icon: Icon }) => (
                  <InfoTooltip key={value} content={t(labelKey)}>
                    <span className="inline-flex shrink-0">
                      <TabsTrigger
                        value={value}
                        onClick={() => navigate(`/${slug}/environments/${deskId}/${value}`)}
                        disabled={!deskId}
                        aria-label={t(labelKey)}
                        className="h-8 min-w-0 shrink-0"
                      >
                        <Icon className="block h-4 w-4 lg:hidden" />
                        <span className="hidden truncate lg:inline">{t(labelKey)}</span>
                      </TabsTrigger>
                    </span>
                  </InfoTooltip>
                ))}
              </TabsList>
            </Tabs>
          </PageSubHeaderTabs>
        }
        actions={
          activeButton ? (
            <PageSubHeaderActions className="min-w-0 shrink-0">
              <Button size="sm" onClick={activeButton.onClick} disabled={activeButton.disabled}>
                <PlusIcon size={16} />
                {t(activeButton.label)}
              </Button>
            </PageSubHeaderActions>
          ) : undefined
        }
      />
      {isReadOnly && <ReadOnlyBanner />}
      <div className="flex min-h-0 flex-1">
        <SettingsNavigation />
        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <Outlet />
        </div>
      </div>
    </Layout>
  );
};

EnvironmentRouter.displayName = 'EnvironmentRouter';

export default EnvironmentRouter;
